import { faMicrophone, faMicrophoneSlash } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { secondsToMilliseconds } from 'date-fns';
import { useAtomValue } from 'jotai';
import { ComponentProps, useCallback } from 'react';

import { useTranslate } from '@growtherapy/i18n';
import { Text, Tooltip } from '@growtherapy/sprout-ui';

import { ControlPanelButton } from '../../components';
import { toast } from '../../toasts';
import {
  LocalMediaStatus,
  localAudioContextAtom,
  useMuteAudio,
  useStoredAudioVideoSettings,
  useUnmuteAudio,
} from '../../video-sdk';
import { getSmoothingFunction, useGetInputVolume } from '../useGetInputVolume';
import { NoAccessButton } from './NoAccess';

const VOLUME_INDICATOR_COUNT = 20;
const volumeSmoothingFunction = getSmoothingFunction({
  maxVolume: VOLUME_INDICATOR_COUNT,
});

const NO_ACCESS_TOAST_KEY = 'no-access-microphone';

type ControlPanelButtonProps = Omit<
  ComponentProps<typeof ControlPanelButton>,
  'aria-label' | 'icon' | 'iconName' | 'iconVariant' | 'iconDefinition'
>;
type AudioControlProps = ControlPanelButtonProps & {
  isEnabled?: boolean;
  loading?: boolean;
  onToggle: (enable: boolean) => void;
  volume?: number;
};

export function AudioControl({ isEnabled, loading, onToggle, volume, ...props }: AudioControlProps) {
  const t = useTranslate();
  const fillPercentage = `${(((volume ?? 0) * 100) / VOLUME_INDICATOR_COUNT).toFixed(0)}%`;

  const label = isEnabled ? t('telehealth-core:controls.toggleAudioOff') : t('telehealth-core:controls.toggleAudioOn');

  return (
    <Tooltip childIsInteractive={!props.disabled} text={label}>
      <ControlPanelButton
        {...props}
        aria-label={label}
        loading={loading}
        className={classNames({ 'text-coral-600': !isEnabled })}
        iconDefinition={isEnabled ? faMicrophone : faMicrophoneSlash}
        data-testid='audio-control'
        onClick={() => onToggle(!isEnabled)}
        style={{
          // no-dd-sa:typescript-code-style/ban-ts-comment
          // @ts-ignore --volume-level is a custom property
          '--volume-level': fillPercentage,
          backgroundImage:
            'linear-gradient(to top, hsl(var(--twc-green-400)) 0%, hsl(var(--twc-green-400)) var(--volume-level), transparent var(--volume-level), transparent 100%)',
        }}
      />
    </Tooltip>
  );
}

type AudioControlWrapperProps = ControlPanelButtonProps & {
  openHelpModal?: () => void;
  showVolumeIndicator?: boolean;
};
export function AudioControlWrapper({ openHelpModal, showVolumeIndicator, ...props }: AudioControlWrapperProps) {
  const t = useTranslate();
  const { delegator: localAudioDelegator, status: localAudioStatus } = useAtomValue(localAudioContextAtom);
  const muteAudio = useMuteAudio();
  const unmuteAudio = useUnmuteAudio();
  const isLoading = localAudioStatus === LocalMediaStatus.LOADING;
  const isEnabled = localAudioDelegator && localAudioStatus === LocalMediaStatus.READY;
  const micVolume = useGetInputVolume({
    smoothingFunction: volumeSmoothingFunction,
  });
  const { setStoredAudioVideoSettings } = useStoredAudioVideoSettings();
  const setStoredAudioControl = useCallback(
    (enabled: boolean) =>
      setStoredAudioVideoSettings((prev) => ({
        ...prev,
        isAudioEnabled: enabled,
      })),
    [setStoredAudioVideoSettings],
  );

  return (
    <AudioControl
      {...props}
      isEnabled={isEnabled}
      loading={isLoading}
      onToggle={async (enable) => {
        if (enable) {
          if (await unmuteAudio()) {
            setStoredAudioControl(true);
            return;
          }
          toast.error(
            <Text>
              {t('telehealth-core:controls.yourBrowserDoesntHaveAccessToYourMicrophone')}{' '}
              <NoAccessButton
                onClick={() => {
                  toast.close(NO_ACCESS_TOAST_KEY);
                  openHelpModal?.();
                }}
              />
            </Text>,
            { key: NO_ACCESS_TOAST_KEY, timeout: secondsToMilliseconds(20) },
          );
          return;
        }
        const result = await muteAudio();
        if (result) {
          setStoredAudioControl(false);
          return;
        }
        toast.error(t('telehealth-core:controls.anUnexpectedErrorOccurredWhileTryingToDisableYourMicrophone'));
      }}
      volume={showVolumeIndicator ? micVolume : 0}
    />
  );
}
